import { type FC, type MouseEvent, useState, useRef } from "react";

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  formattedValue: string;
  color: string;
  type: "asset" | "debt";
}

interface DonutChartProps {
  segments: DonutSegment[];
  centerLabel: string;
  centerValue: string;
  size?: number;
  strokeWidth?: number;
  highlightedKey?: string | null;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  label: string;
  value: string;
}

const DEBT_STRIPE_ID = "debt-stripes";
const ANIM_DURATION = "1500ms";
const ANIM_EASE = "cubic-bezier(0.40,0.00,0.07,0.98)";

export const DonutChart: FC<DonutChartProps> = ({
  segments,
  centerLabel,
  centerValue,
  size = 280,
  strokeWidth = 45,
  highlightedKey,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    label: "",
    value: "",
  });

  const radius = (size - strokeWidth) / 2;
  const center = size / 2;

  const total = segments.reduce((sum, s) => sum + s.value, 0);

  const handleMouseMove = (e: MouseEvent, label: string, value: string) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      label,
      value,
    });
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const emptyRing = (
    <circle
      cx={center}
      cy={center}
      r={radius}
      fill="none"
      stroke="#e7e5e4"
      strokeWidth={strokeWidth}
    />
  );

  let arcsContent = emptyRing;

  if (total > 0) {
    let cumulativeAngle = -90;
    const arcs = segments
      .filter((s) => s.value > 0)
      .map((segment) => {
        const fraction = segment.value / total;
        const angle = fraction * 360;
        const startAngle = cumulativeAngle;
        cumulativeAngle += angle;
        return { ...segment, startAngle, angle, fraction };
      });

    arcsContent = (
      <>
        <defs>
          <pattern
            id={DEBT_STRIPE_ID}
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
            patternTransform="rotate(20)"
          >
            <rect width="8" height="8" fill="#e7e5e4" />
            <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" strokeWidth="2" />
          </pattern>
        </defs>
        {arcs.map((arc) => {
          const isFullCircle = arc.angle >= 359.99;
          const clampedAngle = isFullCircle ? 359.99 : arc.angle;
          const startRad = (arc.startAngle * Math.PI) / 180;
          const endRad = ((arc.startAngle + clampedAngle) * Math.PI) / 180;

          const x1 = center + radius * Math.cos(startRad);
          const y1 = center + radius * Math.sin(startRad);
          const x2 = center + radius * Math.cos(endRad);
          const y2 = center + radius * Math.sin(endRad);

          const largeArc = clampedAngle > 180 ? 1 : 0;

          const d = [`M ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`].join(
            " ",
          );

          const dimmed = highlightedKey != null && highlightedKey !== arc.key;

          return (
            <path
              key={arc.key}
              d={d}
              fill="none"
              stroke={arc.type === "debt" ? `url(#${DEBT_STRIPE_ID})` : arc.color}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
              className="cursor-default transition-opacity duration-200"
              style={{ opacity: dimmed ? 0.2 : 1 }}
              onMouseMove={(e) => handleMouseMove(e, arc.label, arc.formattedValue)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}
        <path
          d={`M ${center} ${center - radius} A ${radius} ${radius} 0 0 0 ${center} ${center + radius} A ${radius} ${radius} 0 0 0 ${center} ${center - radius}`}
          fill="none"
          stroke="white"
          strokeWidth={strokeWidth + 2}
          pathLength={1}
          strokeDasharray={1}
          strokeDashoffset={0}
          style={{
            animation: `donut-wipe ${ANIM_DURATION} ${ANIM_EASE} forwards`,
          }}
        />
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative aspect-square w-full max-h-[400px] max-w-[400px]">
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full">
        {arcsContent}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1">
        <span className="text-sm text-secondary">{centerLabel}</span>
        <span className="text-primary" style={{ fontSize: "30px", fontWeight: 600 }}>
          {centerValue}
        </span>
      </div>
      {tooltip.visible && !highlightedKey && (
        <div
          className="pointer-events-none absolute z-50 rounded-md border border-primary bg-white px-3 py-1.5 shadow-sm"
          style={{ left: tooltip.x + 12, top: tooltip.y - 40 }}
        >
          <p className="text-sm text-primary">{tooltip.label}</p>
          <p className="text-sm font-light text-primary">{tooltip.value}</p>
        </div>
      )}
      {highlightedKey &&
        total > 0 &&
        (() => {
          let cumAngle = -90;
          for (const seg of segments.filter((s) => s.value > 0)) {
            const angle = (seg.value / total) * 360;
            if (seg.key === highlightedKey) {
              const midRad = ((cumAngle + angle / 2) * Math.PI) / 180;
              const outerRadius = radius + strokeWidth / 2 + 16;
              const xPct = ((center + outerRadius * Math.cos(midRad)) / size) * 100;
              const yPct = ((center + outerRadius * Math.sin(midRad)) / size) * 100;
              return (
                <div
                  className="pointer-events-none absolute z-50 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border border-primary bg-white px-3 py-1.5 shadow-sm"
                  style={{ left: `${xPct}%`, top: `${yPct}%` }}
                >
                  <p className="text-sm text-primary">{seg.label}</p>
                  <p className="text-sm font-light text-primary">{seg.formattedValue}</p>
                </div>
              );
            }
            cumAngle += angle;
          }
          return null;
        })()}
    </div>
  );
};
